<template>
  <div class="flex flex-row gap-1 sm:gap-2">
    <!-- Quantity Counter -->
    <div :class="[widthClass, heightClass, disableQuantityControls ? 'opacity-60' : '']" :style="[widthStyle, heightStyle]">
      <div class="flex flex-row justify-between items-center h-full border-2 border-black bg-white">
        <button
          @click="decrementQuantity"
          :disabled="disableQuantityControls"
          class="flex items-center justify-center w-[30px] sm:w-[35px] h-full"
          :class="disableQuantityControls ? 'cursor-not-allowed' : 'hover:bg-black/5 cursor-pointer'"
          :aria-label="$t('cart.decrease')"
        >
          <svg class="w-3 h-3 sm:w-4 sm:h-4" viewBox="0 0 16 16" fill="none">
            <path d="M3.33337 8H12.6667" stroke="#1E1E1E" stroke-width="1.6"/>
          </svg>
        </button>

        <input
          type="number"
          v-model.number="quantity"
          :disabled="disableQuantityControls"
          min="1"
          class="font-archivo font-bold text-sm sm:text-[18px] text-[#010101] text-center w-[60px] sm:w-[70px] h-full border-0 focus:outline-none appearance-none"
          :class="disableQuantityControls ? 'cursor-not-allowed' : ''"
          :aria-label="$t('cart.quantity')"
          style="-moz-appearance: textfield;"
          @focus="$event.target.select()"
        />

        <button
          @click="incrementQuantity"
          :disabled="disableQuantityControls"
          class="flex items-center justify-center w-[30px] sm:w-[35px] h-full"
          :class="disableQuantityControls ? 'cursor-not-allowed' : 'hover:bg-black/5 cursor-pointer'"
          :aria-label="$t('cart.increase')"
        >
          <svg class="w-3 h-3 sm:w-4 sm:h-4" viewBox="0 0 16 16" fill="none">
            <path d="M8 3.33337V12.6667M3.33337 8H12.6667" stroke="#1E1E1E" stroke-width="1.6"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Add Cart Button -->
    <button
      :class="['flex-1 flex justify-center items-center transition-all duration-200', heightClass, disabled ? 'bg-gray-600 cursor-not-allowed opacity-60' : 'bg-black hover:bg-black/90 cursor-pointer']"
      :style="heightStyle"
      :disabled="disabled"
      @click="addToCart"
    >
      <span :class="[
        'font-archivo-narrow font-bold text-[#FFDD00]',
        isProductDetailsPage ? 'text-[18px] sm:text-[24px] leading-[40px] sm:leading-[50px]' : 'text-[15px] sm:text-[20px] leading-[28px] sm:leading-[35px]'
      ]">
        {{ $t('cart.addToCart') }}
      </span>
    </button>
  </div>
</template>

<script setup>
/* eslint-disable no-unused-vars, no-undef */
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

const { t } = useI18n()
const route = useRoute()
const emit = defineEmits(['add-to-cart', 'quantity-changed'])
/* eslint-enable no-unused-vars, no-undef */

// Checks whether the component is being used on the product details page
const isProductDetailsPage = ref(route.name === 'ProductDetails')

// eslint-disable-next-line no-undef
const props = defineProps({
  customHeight: { type: String, default: null },
  customWidth: { type: String, default: null },
  disabled: { type: Boolean, default: false }, // Disables only the Add to Cart button
  disableQuantityControls: { type: Boolean, default: false } // Disables the quantity controls
})

const useStyleHeight = computed(() => !!props.customHeight && props.customHeight.includes('px'))
const heightStyle = computed(() => useStyleHeight.value ? { height: props.customHeight } : {})
const heightClass = computed(() => {
  if (!props.customHeight) return (isProductDetailsPage.value ? 'h-[40px] sm:h-[50px]' : 'h-[28px] sm:h-[35px]')
  return useStyleHeight.value ? '' : props.customHeight
})

const useStyleWidth = computed(() => !!props.customWidth && props.customWidth.includes('px'))
const widthStyle = computed(() => useStyleWidth.value ? { width: props.customWidth } : {})
const widthClass = computed(() => {
  if (!props.customWidth) return 'w-[140px] sm:w-[160px]'
  return useStyleWidth.value ? '' : props.customWidth
})

const quantity = ref(1)

const incrementQuantity = () => {
  quantity.value++
}

const decrementQuantity = () => {
  if (quantity.value > 1) {
    quantity.value--
  }
}

// Validate the manually typed quantity
watch(quantity, (newValue) => {
  // Convert to a number
  let numValue = parseInt(newValue)

  // Check whether it is a valid number
  if (isNaN(numValue) || numValue < 1) {
    quantity.value = 1
  } else {
    // Limit it to a reasonable value to avoid issues (up to 5 characters: 99999)
    if (numValue > 99999) {
      quantity.value = 99999
    } else {
      quantity.value = numValue
    }
  }

  // Emit the quantity change event
  emit('quantity-changed', quantity.value)
})

const addToCart = () => {
  emit('add-to-cart', quantity.value)
}
</script>
<style scoped>
.font-archivo-narrow {
  font-family: 'Archivo Narrow', sans-serif;
}

.font-archivo {
  font-family: 'Archivo', sans-serif;
}

/* Removes the number input arrows in every browser */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type="number"] {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>

